import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { FadeUp, MaskReveal } from "@/components/Motion";
import useSEO, { canonicalFor } from "@/lib/useSEO";

const FIGURE_IMG =
  "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1920&q=80";

export default function FAQ() {
  useSEO({
    title: "Frequently Asked Questions — Corpora Consultants",
    description: "Answers on Cyprus corporate structures, CySEC licensing, MiCA transition, tax residency, banking and immigration — from senior Cyprus counsel.",
    canonical: canonicalFor("/faq"),
  });

  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ categories: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get("/faqs")
      .then((r) => setData(r.data))
      .catch(() => setData({ categories: [], items: [] }))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return data.items;
    return data.items.filter((it) => it.category === activeCategory);
  }, [data.items, activeCategory]);

  // Inject FAQPage JSON-LD schema into document head
  useEffect(() => {
    if (!data.items.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: i18n.language || "en",
      mainEntity: data.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: it.a,
        },
      })),
    };
    const existing = document.getElementById("faq-schema");
    if (existing) existing.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "faq-schema";
    s.textContent = JSON.stringify(schema);
    document.head.appendChild(s);
    return () => {
      const el = document.getElementById("faq-schema");
      if (el) el.remove();
    };
  }, [data.items, i18n.language]);

  // Update page title dynamically for SEO
  useEffect(() => {
    const prev = document.title;
    document.title = `${t("faq.metaTitle")} — Corporaco Consultants`;
    return () => {
      document.title = prev;
    };
  }, [t, i18n.language]);

  return (
    <div data-testid="faq-page">
      <section className="relative h-[55vh] min-h-[440px] overflow-hidden grain">
        <img
          src={FIGURE_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "brightness(0.4) grayscale(0.35)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,11,13,0.7) 0%, rgba(10,11,13,0.45) 35%, rgba(10,11,13,0.96) 100%)",
          }}
        />
        <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20">
          <div className="overline mb-6">{t("faq.eyebrow")}</div>
          <h1
            className="display text-[var(--bone)]"
            style={{ fontSize: "clamp(56px, 9vw, 140px)" }}
          >
            <div>
              <MaskReveal>{t("faq.heroLine1")}</MaskReveal>
            </div>
            <div className="display-italic" style={{ color: "var(--pewter)" }}>
              <MaskReveal delay={0.2}>{t("faq.heroLine2")}</MaskReveal>
            </div>
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="text-lg md:text-xl text-[var(--bone-2)] leading-relaxed max-w-3xl mb-14">
              {t("faq.intro")}
            </p>
          </FadeUp>

          <FadeUp>
            <div
              className="flex flex-wrap gap-3 mb-14 border-b border-[var(--line)] pb-6"
              data-testid="faq-category-filter"
            >
              <button
                onClick={() => setActiveCategory("all")}
                data-testid="faq-cat-all"
                className={`tag ${activeCategory === "all" ? "tag-active" : ""}`}
              >
                {t("faq.allTopics")}
              </button>
              {data.categories.map((c) => (
                <button
                  key={c.id}
                  data-testid={`faq-cat-${c.id}`}
                  onClick={() => setActiveCategory(c.id)}
                  className={`tag ${activeCategory === c.id ? "tag-active" : ""}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </FadeUp>

          {loading && (
            <div className="font-mono text-sm text-[var(--mute)]">
              {t("faq.loading")}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="font-mono text-sm text-[var(--mute)]">
              {t("faq.empty")}
            </div>
          )}

          <div className="divide-y divide-[var(--line)]" data-testid="faq-list">
            {filtered.map((it, idx) => {
              const open = openId === it.id;
              return (
                <div
                  key={it.id}
                  data-testid={`faq-item-${it.id}`}
                  className="py-8 md:py-10"
                >
                  <button
                    onClick={() => setOpenId(open ? null : it.id)}
                    data-testid={`faq-toggle-${it.id}`}
                    className="w-full text-left flex items-start justify-between gap-8 group"
                  >
                    <div className="flex items-baseline gap-6 flex-1">
                      <span className="font-mono text-xs text-[var(--mute-2)] mt-1 shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <h3 className="display text-xl md:text-2xl lg:text-3xl text-[var(--bone)] leading-[1.2] group-hover:text-[var(--pewter)] transition-colors">
                        {it.q}
                      </h3>
                    </div>
                    <ChevronDown
                      size={22}
                      className={`text-[var(--pewter)] shrink-0 mt-2 transition-transform duration-300 ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {open && (
                    <div
                      className="mt-6 pl-0 md:pl-14 text-[var(--bone-2)] leading-relaxed text-base md:text-lg whitespace-pre-line"
                      data-testid={`faq-answer-${it.id}`}
                    >
                      {it.a}
                      <div className="mt-6 pt-6 border-t border-[var(--line)] flex flex-wrap gap-4 items-center">
                        <Link
                          to="/contact"
                          data-testid={`faq-consult-${it.id}`}
                          className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--pewter)] hover:text-[var(--bone)] inline-flex items-center gap-2"
                        >
                          {t("faq.needAdvice")} <ArrowUpRight size={14} />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <FadeUp>
            <div className="mt-24 pt-16 border-t border-[var(--line)] grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="overline mb-4">{t("faq.ctaEyebrow")}</div>
                <h2 className="display text-4xl md:text-5xl text-[var(--bone)] leading-[1.05]">
                  {t("faq.ctaTitleA")}{" "}
                  <span
                    className="display-italic"
                    style={{ color: "var(--pewter)" }}
                  >
                    {t("faq.ctaTitleB")}
                  </span>
                </h2>
              </div>
              <div className="md:text-right">
                <p className="text-[var(--bone-2)] mb-6 md:ml-auto md:max-w-md">
                  {t("faq.ctaBody")}
                </p>
                <Link
                  to="/contact"
                  data-testid="faq-cta-consult"
                  className="btn-primary inline-flex"
                >
                  {t("faq.ctaBtn")}
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
